import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Loader2, ShieldCheck, ShieldX } from "lucide-react";
import api from "../api";
import { fmtDate, useLang } from "../i18n";

const KINDS = ["search", "pep", "form", "ros", "rte", "freeze"];

export default function VerifyDoc() {
  const { number } = useParams();
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/public/verify/${encodeURIComponent(number)}`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [number]);

  return (
    <div className="min-h-screen bg-[#F8FAFE] bg-[radial-gradient(ellipse_at_top_right,#EAF1FC,transparent_55%)]" data-testid="verify-page">
      <div className="absolute inset-0 opacity-50 pointer-events-none" style={{ backgroundImage: "linear-gradient(#DFE8F6 1px, transparent 1px), linear-gradient(90deg, #DFE8F6 1px, transparent 1px)", backgroundSize: "44px 44px" }} />
      <header className="relative z-10 bg-white/85 backdrop-blur border-b border-[#DFE8F6] shadow-sm">
        <div className="max-w-3xl mx-auto px-4 h-[72px] flex items-center gap-3">
          <div className="bg-white rounded-xl border border-[#DFE8F6] shadow-[0_8px_24px_rgba(63,69,79,0.08)] p-1.5">
            <img src="/logo.png" alt="KYCFederal" className="w-10 h-10 object-contain" />
          </div>
          <div className="leading-tight">
            <div className="font-heading font-extrabold tracking-wide text-sm text-[#16335F]">{t("brand.name")}</div>
            <div className="text-[11px] font-semibold text-[#044CBC]">{t("vf.title")}</div>
          </div>
        </div>
      </header>
      <main className="relative z-10 max-w-3xl mx-auto p-4 sm:p-6 lg:p-10 animate-[fadeIn_0.5s_ease-out]">
        {!data && !error && (
          <div className="ec-card p-10 text-center" data-testid="verify-loading"><Loader2 className="w-6 h-6 animate-spin inline text-[#044CBC]" /></div>
        )}
        {error && (
          <div className="ec-card p-10 text-center space-y-3" data-testid="verify-invalid">
            <ShieldX className="w-12 h-12 text-red-500 mx-auto" />
            <h1 className="font-heading text-xl font-extrabold text-slate-900">{t("vf.invalid")}</h1>
            <p className="text-sm text-slate-500 font-mono">{number}</p>
          </div>
        )}
        {data && (
          <div className="ec-card p-8 space-y-5" data-testid="verify-valid">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
              <div>
                <h1 className="font-heading text-xl font-extrabold text-slate-900">{t("vf.valid")}</h1>
                <p className="text-xs text-slate-500">{t("vf.hint")}</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <div><span className="ec-label">{t("vf.number")}</span><span className="font-mono font-bold text-slate-900">{data.number}</span></div>
              <div><span className="ec-label">{t("vf.type")}</span><span className="font-semibold text-slate-800">{KINDS.includes(data.kind) ? t(`vf.kind_${data.kind}`) : data.kind}</span></div>
              <div><span className="ec-label">{t("vf.org")}</span><span className="text-slate-800">{data.tenant_name}</span></div>
              <div><span className="ec-label">{t("vf.date")}</span><span className="text-slate-800">{fmtDate(data.created_at)}</span></div>
              {data.title && <div className="sm:col-span-2"><span className="ec-label">{t("h.name_title")}</span><span className="text-slate-800">{data.title}</span></div>}
            </div>
            <div className="bg-slate-50 rounded-lg p-3 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-[#044CBC] shrink-0" />
              <span className="text-xs font-mono text-slate-600 break-all">{t("vf.seal")}: {data.seal_preview}</span>
            </div>
          </div>
        )}
      </main>
      <footer className="text-center text-[11px] text-slate-400 pb-8">KYCFederal v4.0 SSNF 2026</footer>
    </div>
  );
}
